import React, {
  useState,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { TextBox, Select, Checkbox } from "@veneer/core";
import { Row, Col } from "react-bootstrap";
import type { RootState } from "@store/store";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import { useTranslation } from "react-i18next";
import { setPrimaryFormValidity } from "@store/reducers/form-contact-primary-slice";
import { primaryContactInformation, submitFormRef } from "@constants/case-create";

import {
  getFieldError,
  getValidatedNumberInput,
  isValidEmail,
  isValidField,
  isValidPhoneNumber,
  onSearchDropDown,
  scrollToErrorField,
  validateFields,
  validateName,
} from "../../../../utils";
import { fetchContactDetails } from "@services/fetch-contact-details-info";
import { HelpModalPopup } from "@components/home-page/shared-components/modal-popup/help-modal-popup";
import { toast } from "react-toastify";
import "./case-primary-address.css";

export interface MyComponentProps {
  headerTitle?: string;
  headerContent?: string;
}

type PrimaryContactInformation = typeof primaryContactInformation;

const CasePrimaryAddress = forwardRef<
  submitFormRef,
  MyComponentProps
>(({ headerTitle, headerContent }, ref) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { savedContactsDropDown, countryList, countryCodes, } = useAppSelector(
    (state: RootState) => state.createCaseAPI
  );
  const formPrimaryData = useAppSelector(
    (state) => state.formContactPrimaryInfo.formPrimaryData
  );
  const [filteredAltContacts, setFilteredAltContacts] = useState();

  const [filteredCountryList, setFilteredCountryList] = useState(
    countryList || []
  );

  const [filteredCountryCodes, setFilteredCountryCodes] = useState(
    countryCodes || []
  );
  const [filteredAltCountryCodes, setFilteredAltCountryCodes] = useState(
    countryCodes || []
  );

  const [formDetails, setFormDetails] =
  useState<PrimaryContactInformation>(formPrimaryData);
  const [errors, setErrors] = useState<PrimaryContactInformation>(
    primaryContactInformation
  );
  const [formTouched, setFormTouched] = useState(true);

  const [popupTilte, setPopupTitle] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [clear, setClear] = useState(false);

  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [flgBrazil, setflgBrazil] = useState({
    neighbourhood: true,
    stateInscription: true,
    cpf: true
  });

  useEffect(() => {
    if (countryList !== null) {
      setFilteredCountryList(countryList)
    }
  }, [countryList])

  const defaultRequiredFields = ["FirstName", "LastName", "countryId", "PhoneNumber", "Email"];

  const requiredFieldsRef = useRef<string[]>(defaultRequiredFields);
  const getUserId = () => formDetails?.UserId?.[0] ?? "";

  useEffect(() => {
    if (countryCodes != undefined && countryList != undefined) {
      setFilteredCountryCodes(countryCodes);
      setFilteredAltCountryCodes(countryCodes);
      setFilteredCountryList(countryList);
      setFormDetails(formPrimaryData)

    }
    if (savedContactsDropDown?.length) {
      let savedContacts: any = [
        { label: t('HPI.Dropdown.SelectSavedContact', { defaultValue: 'create a new contact' }), value: "-1" },
        ...savedContactsDropDown
      ];
      setFilteredAltContacts(savedContacts);
      // setFormDetails((prevData) => ({ ...prevData, UserId: ["-1"] }));
    }
  }, [savedContactsDropDown, countryCodes, countryList]);
  useEffect(() => {
     const userId = getUserId()
    if (formDetails && !clear && userId && userId.length > 0) {
      const userId = getUserId();

      const isDisabled = userId !== "-1";
      setIsDisabled(isDisabled);
    }
    if(userId === "" || userId === undefined){
      setIsDisabled(true)
    }
  }, [formDetails, clear]);


  useEffect(() => {
    setflgBrazil({
      neighbourhood: (formDetails?.neighbourhood === "") ? false : true,
      stateInscription: (formDetails?.stateInscription === "") ? false : true,
      cpf: (formDetails?.cpf === "") ? false : true,
    });

  }, []);


  useEffect(() => {
    if (formTouched) {
      const isValid = false;
      dispatch(
        setPrimaryFormValidity({
          isValid,
          formData: formDetails,
        })
      );
    }
  }, [formDetails]);

  useEffect(() => {
    const specialCountryId = "a2ec3ba9-f09c-e611-80f0-3863bb2e4470";
    requiredFieldsRef.current = formDetails?.countryId?.[0] === specialCountryId
      ? [...defaultRequiredFields, "neighbourhood", "stateInscription", "cpf"]
      : defaultRequiredFields;
  }, [formDetails?.countryId?.[0]]);


  useImperativeHandle(ref, () => ({
    submitForm: () => {
      let errorText = '';

      if (formDetails.AlternatePhoneNumber?.length > 0) {
        const specialCountryId = "a2ec3ba9-f09c-e611-80f0-3863bb2e4470";
        requiredFieldsRef.current = formDetails?.countryId?.[0] === specialCountryId
          ? [...defaultRequiredFields, 'AlternatePhoneNumber', "neighbourhood", "stateInscription", "cpf"]
          : [...defaultRequiredFields, 'AlternatePhoneNumber'];
      } else {
        requiredFieldsRef.current = requiredFieldsRef.current.filter(
          (field) => field !== 'AlternatePhoneNumber'
        );
      }

      const isValidPhone = isValidPhoneNumber(formDetails.PhoneNumber);
      const isValid = validateForm(formDetails);
      const userId = formDetails?.UserId?.[0] ?? "";
      if (!isValidPhone && userId !== "-1" && userId !== "") {
        errorText = 'Phone number is invalid. Please create a new address.';
        return { isValid: false, errorText };
      }
       
      if (!isValid) {
        errorText = t('HPI.ErrorMessage.FillRequiredFields', { defaultValue: 'Please fill the required fields of primary contact' });
      } else {
        dispatch(setPrimaryFormValidity({ isValid, formData: formDetails }));
      }

      return { isValid, errorText };
    },
  }));


  const fetchContactInfo = useCallback(async (payload: any) => {
    try {
      const data = await fetchContactDetails(dispatch, payload);
      setflgBrazil({
        neighbourhood: (data?.neighbourhood === "") ? false : true,
        stateInscription: (data?.stateInscription === "") ? false : true,
        cpf: (data?.cpf === "") ? false : true,
      });
      setFormDetails((prevData) => ({ ...prevData, ...data }));
    } catch (err) {
      // setError("Failed to load contact details");
    }
  }, []);

  const validateForm = (formDetails: PrimaryContactInformation) => {
    let formIsValid = true;
    let firstErrorField: string | null = null;
  
    const userId = formDetails?.UserId?.[0] ?? "";
    console.log(formDetails?.UserId?.[0],"ggg")
  if (userId === "" || userId === undefined) {
    setErrors((prevState) => ({
      ...prevState,
      UserId: [
        t('HPI.ErrorMessage.RequiredFields', {
          defaultValue: 'This field is required.',
        }),
      ],
    }));
    formIsValid = false;
    firstErrorField = "UserId";
  } else {
    setErrors((prevState) => ({
      ...prevState,
      UserId: [""],
    }));
  }
    for (const key in formDetails) {
      if (formDetails.hasOwnProperty(key)) {
        const value = formDetails[key as keyof PrimaryContactInformation];
        const valueToValidate = Array.isArray(value) ? value.join(" ") : value;
        const error = validateFields(key, valueToValidate, requiredFieldsRef.current, t);
        if (error) {
          setErrors((prevState) => ({
            ...prevState,
            [key]: error,
          }));
          formIsValid = false;
          if (!firstErrorField) {
            firstErrorField = key;
          }
        } else {
          if (
            Array.isArray(formDetails[key as keyof PrimaryContactInformation])
          ) {
            setErrors((prevState) => ({
              ...prevState,
              [key]: [""],
            }));
          } else {
            setErrors((prevState) => ({
              ...prevState,
              [key]: "",
            }));
          }
        }
      }
    }
    if (!formIsValid && firstErrorField) {
      scrollToErrorField(firstErrorField)
    }
    return formIsValid;
  };

  const selectSavedClearHandler = () => {
    setClear(true);
    setIsDisabled(true);
    setFormDetails({ ...primaryContactInformation,UserId: [""], });
  };

  const popupHeader = () => {
    return (
      <p className="popup-header">
        <img
          src="https://logos-world.net/wp-content/uploads/2020/11/HP-Logo.png"
          alt=""
          style={{ height: "46px", width: "auto" }}
        />
        <span>{popupTilte}</span>
      </p>
    );
  };

  const handleCallBack = (title: string) => {
    setPopupTitle(title);
    setOpen(true);
  };

  const onSelectChangeHandler = (selectedOption: any, k: any) => {
    setFormTouched(true);
    setflgBrazil({
      neighbourhood: false,
      stateInscription: false,
      cpf: false
    })
    setErrors(primaryContactInformation);
    setClear(false);
    if (selectedOption?.value !== "-1") {
      setFormDetails((prevData) => ({
        ...prevData,
        [k]: [selectedOption?.value],
      }));

      if (k === 'countryId') {
        setFormDetails((prevData) => ({ ...prevData, ['countryName']: selectedOption?.CountryName }));
      }
    } else {
      setIsDisabled(true);
      setFormDetails({ ...primaryContactInformation });
      setFormDetails((prevData) => ({ ...prevData, [k]: ["-1"] }));
    }

    if (k === "UserId" && selectedOption.value !== "-1") {

      setIsDisabled(true);

      const payload = {
        contactid: selectedOption.value,
      };

      fetchContactInfo(payload);
    } else {
      setIsDisabled(false);
    }

    validateField(k, selectedOption?.value);
  };

  const handleInputChange = (
    v: any,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!formTouched) setFormTouched(true);
    const { name, value } = event.target;
    setFormDetails((prevData) => ({ ...prevData, [name]: v }));

    if (['PhoneNumber', 'Email', 'AlternatePhoneNumber'].includes(name)) {
      setErrors((prevState) => ({
        ...prevState,
        [name]: '',
      }));
    } else {
      validateField(name, v);
    }

  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value) {
      validateField(name, value);
    }
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    if (!value) {
      error = t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' });
    }

    // if (name === "FirstName" && isValidField(value)) {
    //   error = t('HPI.ErrorMessage.InvalidFirstName',{defaultValue:'Invalid First Name'});
    // }

    // if (name === "LastName" &&  isValidField(value)) {
    //   error = "Invalid Last Name";
    // }

    if (
      name === "Email" && value && !isValidEmail(value)
    ) {
      error = t('HPI.ErrorMessage.InvalidEmailFormat', { defaultValue: 'Invalid email format' });
    }

    if ((name === "PhoneNumber" || name === "AlternatePhoneNumber") && !isValidPhoneNumber(value)) {
      error = t('HPI.ErrorMessage.InvalidNumber', { defaultValue: 'Phone number must be 8-15 digits long and contain only numbers.' });
    }

    setErrors((prevState) => ({
      ...prevState,
      [name]: error,
    }));

  };

  const clearSelectHandler = (field: any) => {
    setFormDetails((prevData) => ({ ...prevData, [field]: [""] }));
  };

  const onAddsavedcontactsHandler = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;
    setFormDetails((prevData) => ({
      ...prevData,
      ["addsavedcontacts"]: checked ? "true" : "false",
    }));
  };

  const isDisabledFeild = (val: any) => {
    return val !== "-1" && val !== ""
  }

  return (
    <>
      {headerTitle ? (
        <div className="">
          <h4>{headerTitle}</h4>
          <h5>{headerContent}</h5>
          {/* <p className="label-small" id="RequiredFields">
            Required Fields <span className="text-danger">*</span>{" "}
          </p> */}
        </div>
      ) : (
        ""
      )}

      <Row className="">
        {
          <Col md={6}>
            <Select
              showSearch
              placement="bottom"
              options={filteredAltContacts || []}
              label={`${t("HPI.CreateCase.Contactinformation.savedContact", { defaultValue: 'Saved Contact -' })} ${t("HPI.CreateCase.Contactinformation.choosefromlist", { defaultValue: 'Choose a saved contact or create a new contact' })}`}
              helperTextVisibility="auto"
              separateLabel
              name="UserId"
              className="textbox"
              onChange={(selectedOption: any) =>
                onSelectChangeHandler(selectedOption, "UserId")
              }
              value={formDetails?.UserId || []}
              placeholder={t('HPI.Dropdown.DefaultText', { defaultValue: 'Please select' })}
              onSearch={(value: any) => {
                let filtered: any = onSearchDropDown(savedContactsDropDown, value);

                let savedContacts: any = [
                  { label: t('HPI.Dropdown.SelectSavedContact', { defaultValue: 'create a new contact' }), value: "-1" },
                  ...filtered
                ];
                setFilteredAltContacts(savedContacts);
              }}
              onClear={() => {
                selectSavedClearHandler();
              }}
              {...getFieldError(errors, "UserId")}
            />
          </Col>
        }
      </Row>

      <Row>
        <Col md={6}>
          <TextBox
            required
            separateLabel
            label={t("HPI.CreateCase.Contactinformation.Firstname", { defaultValue: 'First Name' })}
            disabled={isDisabled}
            name="FirstName"
            placeholder={t('HPI.Profilepage.Placeholder.EnterFirstName', { defaultValue: 'Enter first name' })}
            onChange={(v: any, e) => handleInputChange(v, e)}
            value={formDetails.FirstName}
            error={!!errors.FirstName}
            helperText={errors.FirstName}
            className="textbox"
          />
        </Col>

        <Col md={6}>
          <TextBox
            required
            separateLabel
            label={t("HPI.CreateCase.Contactinformation.Lastname", { defaultValue: 'Last Name' })}
            disabled={isDisabled}
            name="LastName"
            placeholder={t('HPI.Profilepage.Placeholder.EnterLastName', { defaultValue: 'Enter last name' })}
            onChange={(v: any, e) => handleInputChange(v, e)}
            value={formDetails.LastName}
            error={!!errors.LastName}
            helperText={errors.LastName}
            className="textbox"
          />
        </Col>

        {/* below is for country code  */}

        <Col md={6} lg={2}>
          <Select
            separateLabel
            options={filteredCountryCodes}
            disabled={isDisabled}
            showSearch
            required
            name="countryId"
            label={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
            placement="bottom"
            placeholder={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
            value={formDetails?.countryId || []}
            helperTextVisibility="auto"
            onClear={() => {
              clearSelectHandler("countryId");
            }}
            onChange={(selectedOption: any) =>
              onSelectChangeHandler(selectedOption, "countryId")
            }
            {...getFieldError(errors, "countryId")}
            onSearch={(value: any) => {
              let filtered: any = onSearchDropDown(countryCodes, value);
              setFilteredCountryCodes(filtered);
            }}
            className="textbox country-code"
          />
        </Col>
        <Col lg={4} md={6} >
          <label className="custom-label">{t("HPI.CreateCase.Contactinformation.Phonenumber", { defaultValue: 'Phone Number' })}
            {!isDisabled && <span className="error">*</span>}
          </label>
          <span className="supporting-text" style={{ top: "0px" }}>
            <HelpModalPopup
              align={"start"}
              id={"modal-popup"}
              show={open}
              onClose={() => setOpen(false)}
              title={popupHeader()}
              callback={() => handleCallBack(t('HPI.CreateCase.Contactinformation.Phonenumber', { defaultValue: 'Phone Number' }))}
            >
              <p>
                {t('HPI.Profilepage.PhoneNumberToolTip.body', { defaultValue: `Please enter no more than 15 numerals, starting with country code (do not use '+' or any other characters)` })}

              </p>
            </HelpModalPopup>
          </span>
          <TextBox
            placeholder={t('HPI.CreateCase.Placeholder.EnterPhoneNumber', { defaultValue: 'Enter phone number' })}
            separateLabel
            required
            disabled={isDisabled}
            //label={t("HPI.CreateCase.Contactinformation.Phonenumber",{defaultValue:'Phone Number'})}
            value={formDetails.PhoneNumber}
            name="PhoneNumber"
            onChange={(v: any, e) => {
              const value = getValidatedNumberInput(v, 15);
              if (value.length <= 15) {
                handleInputChange(value, e);
              }
            }}
            onBlur={(e) => handleBlur(e)}
            error={!!errors.PhoneNumber}
            helperText={errors.PhoneNumber}
            className="textbox"
          />
        </Col>

        <Col md={6}>
          <TextBox
            value={formDetails.Extension}
            label={t("HPI.CreateCase.Contactinformation.Extension", { defaultValue: 'Extension' })}
            name="Extension"
            placeholder={t('HPI.CreateCase.Placeholder.EnterExtension', { defaultValue: 'Enter extension' })}
            separateLabel
            disabled={isDisabled}
            className="textbox"
            onChange={(v: any, e) => {
              const value = getValidatedNumberInput(v, 5);
              if (value.length <= 5) {
                handleInputChange(value, e);
              }
            }}
          />
        </Col>



        <Col lg={2} md={6}>
          <Select
            separateLabel
            className="textbox country-code"
            placement="bottom"
            options={filteredAltCountryCodes}
            showSearch
            disabled={isDisabled}
            name="phNumAlternateCountryCode"
            label={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
            placeholder={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
            value={formDetails.phNumAlternateCountryCode || []}
            helperTextVisibility="auto"
            onClear={() => {
              clearSelectHandler("phNumAlternateCountryCode");
            }}
            onChange={(selectedOption: any) =>
              onSelectChangeHandler(selectedOption, "phNumAlternateCountryCode")
            }
            onSearch={(value: any) => {
              let filtered: any = onSearchDropDown(countryCodes, value);
              setFilteredAltCountryCodes(filtered);
            }}

          />
        </Col>
        <Col lg={4} md={6}>
          <label className="label-contact">{t("HPI.CreateCase.contactinformation.alternatehelpdesk", { defaultValue: 'Alternate Phone Number' })}
            {/* <span className="error">*</span> */}
          </label>
          <span className="supporting-text" style={{ top: '-5px' }}>
            <HelpModalPopup
              align={"start"}
              id={"modal-popup"}
              show={open}
              onClose={() => setOpen(false)}
              title={popupHeader()}
              callback={() => handleCallBack(t('HPI.CreateCase.Contactinformation.Phonenumber', { defaultValue: 'Phone Number' }))}
            >
              <p>
                {t('HPI.Profilepage.PhoneNumberToolTip.body', { defaultValue: `Please enter no more than 15 numerals, starting with country code (do not use '+' or any other characters)` })}

              </p>
            </HelpModalPopup>
          </span>
          <TextBox
            placeholder={t('HPI.Profilepage.Placeholder.EnterAlternatePhoneNumber', { defaultValue: 'Enter alternate phone number' })}
            separateLabel
            disabled={isDisabled}
            //label={t("HPI.CreateCase.contactinformation.alternatehelpdesk",{defaultValue:'Alternate Phone Number'})}
            value={formDetails.AlternatePhoneNumber}
            name="AlternatePhoneNumber"
            onChange={(v: any, e) => {
              const value = getValidatedNumberInput(v, 15);
              if (value.length <= 15) {
                handleInputChange(value, e);
              }
            }}
            onBlur={(e) => handleBlur(e)}
            error={!!errors.AlternatePhoneNumber}
            helperText={errors.AlternatePhoneNumber}
            className="textbox"
          />
        </Col>

        <Col md={6}>
          <TextBox
            value={formDetails.AlternateExtension}
            label={t("HPI.CreateCase.Contactinformation.Extension", { defaultValue: 'Extension' })}
            disabled={isDisabled}
            name="AlternateExtension"
            placeholder={t('HPI.CreateCase.Placeholder.EnterExtension', { defaultValue: 'Enter extension' })}
            separateLabel
            className="textbox"
            onChange={(v: any, e) => {
              const value = getValidatedNumberInput(v, 5);
              if (value.length <= 5) {
                handleInputChange(value, e);
              }
            }}
          />
        </Col>


        <Col md={6}>
          <Select
            required
            showSearch
            placement="bottom"
            options={filteredCountryList}
            label={t("HPI.CreateCase.address.CountryRegion", { defaultValue: 'Country/Region' })}
            helperTextVisibility="auto"
            separateLabel
            disabled={isDisabled}
            name="countryId"
            onChange={(selectedOption: any) =>
              onSelectChangeHandler(selectedOption, "countryId")
            }
            value={formDetails?.countryId || []}
            placeholder={t('HPI.Dropdown.DefaultText', { defaultValue: 'Please select' })}
            onSearch={(value: any) => {
              let filtered: any = onSearchDropDown(countryList, value);
              setFilteredCountryList(filtered);
            }}
            onClear={() => {
              clearSelectHandler("countryId");
            }}
            {...getFieldError(errors, "countryId")}
            className="textbox"
          />
        </Col>

        <Col md={6}>
          <TextBox
            separateLabel
            label={t("HPI.CreateCase.Contactinformation.Email", { defaultValue: 'Email' })}
            type="email"
            disabled={isDisabled}
            name="Email"
            required
            placeholder={t('HPI.CreateCase.Placeholder.EnterEmail', { defaultValue: 'Enter email' })}
            value={formDetails.Email}
            onChange={(v: any, e) => handleInputChange(v, e)}
            onBlur={(e) => handleBlur(e)}
            error={!!errors.Email}
            helperText={errors.Email}
            className="textbox"
          />
        </Col>
      </Row>

      {(formDetails?.countryId || []).includes(
        "a2ec3ba9-f09c-e611-80f0-3863bb2e4470"
      ) && (
          <>
            <Row>
              <Col md={6}>
                <TextBox
                  separateLabel
                  // disabled={isDisabledFeild(formDetails.neighbourhood)}
                  label={t("HPI.CreateCase.Contactinformation.Neighborhood", { defaultValue: 'Neighborhood' })}
                  required
                  disabled={flgBrazil.neighbourhood}
                  name="neighbourhood"
                  placeholder={t('HPI.CreateCase.Placeholder.EnterNeighborhood', { defaultValue: 'Enter neighborhood' })}
                  onChange={(v: any, e) => handleInputChange(v, e)}
                  value={formDetails.neighbourhood}
                  error={!!errors.neighbourhood}
                  helperText={errors.neighbourhood}
                  className="textbox"
                />
              </Col>

              <Col md={6}>
                <TextBox
                  separateLabel
                  required
                  disabled={flgBrazil.cpf}
                  label={t("HPI.CreateCase.Contactinformation.CPF_CNPJ", { defaultValue: 'CPF CNPJ' })}
                  name="cpf"
                  placeholder={t('HPI.CreateCase.Placeholder.EnterCPF/CNPJ', { defaultValue: 'Enter CPF/CNPJ' })}
                  // disabled={isDisabledFeild(formDetails.cpf)}
                  onChange={(v: any, e) => handleInputChange(v, e)}
                  value={formDetails.cpf}
                  error={!!errors.cpf}
                  helperText={errors.cpf}
                  className="textbox"
                />
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <TextBox
                  separateLabel
                  label={t("HPI.CreateCase.Contactinformation.State_Inscription", { defaultValue: 'State Inscription' })}
                  required
                  //disabled={flgBrazil.stateInscription ? true : isDisabled }
                  disabled={flgBrazil.stateInscription}
                  name="stateInscription"
                  placeholder={t('HPI.CreateCase.Placeholder.EnterStateInscription', { defaultValue: 'Enter state inscription' })}
                  onChange={(v: any, e) => handleInputChange(v, e)}
                  value={formDetails.stateInscription}
                  error={!!errors.stateInscription}
                  helperText={errors.stateInscription}
                  className="textbox"
                />
              </Col>
            </Row>
          </>
        )}

      {!isDisabled && <div className="row">
        <div className="checkbox-container">
          <Col md={6}>
            <Checkbox
              name={formDetails.addsavedcontacts}
              onChange={onAddsavedcontactsHandler}
              label={t("HPI.CreateCase.Contactinformation.addthiscontactheading", { defaultValue: 'Add this contact to my saved contacts' })}
            />
          </Col >
          <Col md={6}>
            <p id="RequiredFields">
              Required Fields {" "}<span className="text-danger">*</span>{" "}
            </p>
          </Col>
        </div>
      </div>}
    </>
  );
});

export default CasePrimaryAddress;
